/**
 * Form validation utilities and schemas for Vertiv POC application.
 *
 * **Figma (Visual Template)** – form validation / error state:
 * `https://www.figma.com/design/nzRrn94pPJjuXdhfsuRMN6/Visual-Template?node-id=7558-17274` (m=dev)
 */

import {
  OPPORTUNITY_PROJECT_DESCRIPTION_MAX,
  validateOppDetails,
} from '../validation/opportunityProjectFieldsSchema';
import { validateRequestFormSections } from '../validation/requestFormSectionSchemas';

export interface FormValidationError {
  field: string;
  message: string;
}

export interface FormValidationResult {
  isValid: boolean;
  errors: FormValidationError[];
}

/**
 * Required field copy for the legacy Opportunity path (no `createRequestProjectDetails`).
 * Values are full sentences; `formatValidationErrors` only appends " is required" when
 * the message does not end with . ! ?
 */
export const REQUIRED_FIELDS = {
  region: 'Select a region.',
  requestorEmail: 'Enter a work email address.',
  customerName: 'Enter a customer name.',
} as const;

/** Reserved for legacy flows; PD submit uses {@link validateOppDetails} (region + customer required among project fields). */
export const REQUIRED_FIELDS_WITH_PROJECT_TYPE = {
  oppProjectType: 'Select a project type.',
} as const;

/**
 * Validates email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const PHONE_MAX_LENGTH = 50;
const PHONE_PATTERN = /^[+\d\s\-().]*$/;

/**
 * Validates phone number format (allows various international formats)
 */
export const validatePhone = (phone: string): boolean => {
  // Remove all non-numeric characters for validation
  const cleanPhone = phone.replace(/\D/g, '');
  // Must be at least 10 digits
  return cleanPhone.length >= 10;
};

/**
 * Optional requestor phone — empty is valid; non-empty must match format rules.
 * Returns a full-sentence message suitable for inline field errors.
 */
export const validateRequestorPhone = (phone: string): string | undefined => {
  const val = phone ?? '';
  if (val === '') {
    return undefined;
  }
  if (val.length > PHONE_MAX_LENGTH) {
    return `Phone must be ${String(PHONE_MAX_LENGTH)} characters or less`;
  }
  if (!PHONE_PATTERN.test(val)) {
    return 'Phone can only contain numbers, spaces, and +()-. ';
  }
  if (!validatePhone(val)) {
    return 'Use a valid phone number (at least 10 digits).';
  }
  return undefined;
};

/**
 * Validates Opportunity / Site Walk fields (Zod) plus all other request-form sections
 * (site dimensions through feedback — see `requestFormSectionSchemas.ts`).
 */
export const validateRequiredFields = (
  formData: Record<string, unknown>,
): FormValidationResult => {
  const opp = validateOppDetails(formData);
  const sections = validateRequestFormSections(formData);
  const errors = [...opp.errors, ...sections.errors];
  return { isValid: errors.length === 0, errors };
};

/** If the copy is already a full sentence, do not append " is required" in `formatValidationErrors`. */
function isCompleteValidationMessage(message: string): boolean {
  const t = message.trim();
  if (t.length === 0) {
    return false;
  }
  const c = t[t.length - 1];
  if (c === '.' || c === '!' || c === '?') {
    return true;
  }
  return t.toLowerCase().endsWith('is required');
}

/** Cap bullet list length in toasts/banners; remainder summarized without echoing full text. */
export const VALIDATION_ERROR_LIST_MAX_ITEMS = 12;

function normalizeValidationLine(message: string): string {
  const t = message.trim();
  return t.length > 0 ? t : 'This field needs attention.';
}

/**
 * Formats validation errors for display (toasts, banners). Prefer full-sentence `message` values
 * (ending with . ? !) from Zod / REQUIRED_FIELDS so this does not add " is required" twice.
 *
 * Multiple errors render as a short heading plus one bullet per line (`\\n• `) so web/native
 * toasts stay readable (avoid a single semicolon-separated paragraph).
 */
export const formatValidationErrors = (
  errors: FormValidationError[],
): string => {
  if (errors.length === 0) {
    return '';
  }

  if (errors.length === 1) {
    const m = errors[0].message;
    return isCompleteValidationMessage(m) ? m : `${m} is required`;
  }

  const capped = errors.slice(0, VALIDATION_ERROR_LIST_MAX_ITEMS);
  const bullets = capped
    .map(e => `• ${normalizeValidationLine(e.message)}`)
    .join('\n');
  const omitted = errors.length - capped.length;
  const suffix =
    omitted > 0
      ? `\n• …and ${omitted} more ${omitted === 1 ? 'issue' : 'issues'}.`
      : '';
  return `Please correct the following:\n${bullets}${suffix}`;
};

export const PROJECT_DESCRIPTION_MAX_LENGTH =
  OPPORTUNITY_PROJECT_DESCRIPTION_MAX;

/**
 * Gets the error message for a specific field
 */
export const getFieldError = (
  errors: FormValidationError[],
  fieldName: string,
): string | undefined => {
  const error = errors.find(err => err.field === fieldName);
  return error?.message;
};
